import { deburr } from 'lodash-es'
import { type IValidator, required } from 'validx'
import { mustHaveMaxLength, mustHaveMinLength } from '@/utils/validx-util'
import type { Validatable } from './validation'

const ID_LENGTH_MESSAGE = 'ID must be between 3 and 64 characters'

/**
 * Turns text into an ID: uppercase, with underscores for anything that isn't
 * alphanumeric. Prefixed when it would otherwise not start with a letter.
 *
 * @param prefix
 * @param text
 */
export function generateIdFromText(prefix: string, text: string): string {
  if (!text) {
    return ''
  }

  const newId = deburr(text)
    .toUpperCase()
    .replace(/\s/g, '_')
    .replace(/[',]/g, '')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .trim()
    // FOOTGUN: only the first run collapses. HQ's builder generates IDs the
    // same way, and IDs generated from the same text must match.
    .replace(/_{3,}/, '__')
    .replace(/^[_]+/, '')
    .replace(/[_]+$/, '')

  if (!startsWithValidCharacter(newId)) {
    return `${prefix}_${newId}`
  }

  return newId
}

/**
 * Whether the ID starts with a valid character. Underscores are valid
 * starting characters for the engine, but we don't want people to use them.
 *
 * FOOTGUN: only uppercase letters and a lowercase "a" pass, a typo in HQ's
 * builder that this copies on purpose. HQ renames IDs that fail this when it
 * loads a legend, without updating the formulas that reference them, so a
 * wider rule here would let this editor save IDs that HQ then breaks. Widen it
 * to all letters once HQ's builder is retired.
 *
 * @param id
 */
export function startsWithValidCharacter(id: string): boolean {
  return /^[A-Za]/.test(id)
}

/**
 * IDs are alphanumeric with underscores, and start with a letter. Empty IDs
 * pass; required-ness is checked separately.
 */
function validId<T>(): IValidator<T> {
  return function validateId({ value }) {
    if (typeof value !== 'number' && !value) {
      return true
    }
    const text = String(value)
    if (!startsWithValidCharacter(text)) {
      return 'IDs must start with a letter.'
    }
    return (
      /^[a-zA-Z0-9_]+$/.test(text) ||
      'IDs are alphanumeric and can contain underscores ("_")'
    )
  }
}

/**
 * Something whose ID must be unique among the legend's entities.
 */
export interface IdentifiedEntity extends Validatable {
  readonly cid: string
  id: string
}

/**
 * Validators that keep IDs well-formed and unique, and a setter that only
 * assigns valid IDs.
 *
 * @param getCollection Returns the entities to check the ID against.
 */
export function createEntityIdValidation<T extends IdentifiedEntity>(
  getCollection: (entity: T) => readonly { id?: string; cid?: string }[],
) {
  const validators: IValidator<T>[] = [
    required(ID_LENGTH_MESSAGE),
    mustHaveMinLength(3, ID_LENGTH_MESSAGE),
    mustHaveMaxLength(64, ID_LENGTH_MESSAGE),
    validId(),
    ({ value, obj }) => isNotDuplicate(obj, value),
  ]

  function isNotDuplicate(entity: T, value: unknown): true | string {
    const upped = String(value ?? '').toUpperCase()
    const clash = getCollection(entity).some(
      (x) => x.cid !== entity.cid && !!x.id && x.id.toUpperCase() === upped,
    )
    return clash ? 'Duplicate IDs are not allowed.' : true
  }

  /**
   * Assigns the ID only when it is valid, so a typo never leaves the legend
   * with a broken reference. Setting the current ID again clears the error of
   * an earlier attempt.
   *
   * @returns Whether the ID changed.
   */
  function trySetId(entity: T, id: string): boolean {
    entity.validation.clearErrors('id')
    if (id === entity.id) {
      return false
    }
    const error = validators
      .map((validator) => validator({ field: 'id', value: id, obj: entity }))
      .find((result) => result !== true)
    if (error !== undefined) {
      entity.validation.addErrors({ id: [error || 'The ID is invalid.'] })
      return false
    }
    entity.id = id
    return true
  }

  return { validators, trySetId }
}
